import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import Typography from '@mui/material/Typography'
import { FormikProps } from 'formik'

import { EnrichedInputPort, EnrichedOutputPort } from '../../../api/nm-types'
import { useAppliancePhysicalPorts } from '../../../utils'
import { Api } from '../../../store'
import { Appliance, Group, IpPortMode, PhysicalPort, PortMode } from 'common/api/v1/types'
import { GridItem } from '../../common/Form'
import { initialInputLogicalPort } from '../../inputs/Edit/InputForm'
import { initialOutputLogicalPort } from '../../outputs/Edit/OutputForm'

import {
  ApplianceSectionForm,
  areMultipleLogicalPortsPerApplianceSupported,
  Error,
  Loading,
  LogicalPortForm,
} from './Base'
import { PortDirection } from 'common/types'
import { defaultFrameRate, defaultResolution, defaultScanMode, defaultTimestampResolution } from 'common/api/defaults'
import { whyIsPhysicalPortUnavailable } from '../../../utils/physicalPortUtil'

interface Props {
  namePrefix: string
  isInputForm: boolean
  inputId: string | undefined
  outputId: string | undefined
  adminStatus: 0 | 1
  groupId: Group['id']
  selectedAppliance: Pick<Appliance, 'id' | 'name' | 'type'> & Partial<Pick<Appliance, 'settings'>>
  logicalPorts: Array<EnrichedInputPort | EnrichedOutputPort>
  enforcedPortMode: PortMode | undefined
  isModeSelectionDisabled: boolean
  onAddLogicalPortRequested: () => void
}

const Component = <T extends ApplianceSectionForm>(
  props: FormikProps<T> & Props,
  ref: React.Ref<{
    onAddInterfaceButtonClicked: () => void
  }>,
) => {
  const {
    namePrefix,
    isInputForm,
    inputId,
    outputId,
    groupId,
    adminStatus,
    selectedAppliance,
    logicalPorts,
    enforcedPortMode,
    isModeSelectionDisabled,
    setFieldValue,
    setFieldError,
    onAddLogicalPortRequested,
  } = props
  const logicalPort1 = logicalPorts[0]
  const areMultipleInterfacesSupported = areMultipleLogicalPortsPerApplianceSupported({
    isInput: isInputForm,
    appliance: selectedAppliance,
    portMode: logicalPort1?.mode,
  })

  const {
    result: appliancePhysicalPorts,
    loading: isFetchingPhysicalPorts,
    error: listPhysicalPortsError,
  } = useAppliancePhysicalPorts(
    selectedAppliance,
    Api.appliancesApi,
    isInputForm ? PortDirection.input : PortDirection.output,
    isInputForm ? inputId : outputId,
  )

  useImperativeHandle(ref, () => ({
    onAddInterfaceButtonClicked: addNewLogicalPortToForm,
  }))

  // --> Begin hack that should be removed when the field order has been changed
  // from appliance -> interface -> mode to appliance -> mode -> interface
  // This ensures the loopback interface is always used for generators
  const mode = logicalPort1?.mode
  useEffect(() => {
    const isEnrichedPortLocalInterface = (p: typeof logicalPort1) => p?._port?.addresses?.[0]?.address === '127.0.0.1'
    const isLocalInterface = (p: PhysicalPort) => p.addresses?.[0]?.address === '127.0.0.1'
    const localInterface = appliancePhysicalPorts?.find(isLocalInterface)

    if (mode === IpPortMode.generator && localInterface && !isEnrichedPortLocalInterface(logicalPort1)) {
      const generatorInputPort: EnrichedInputPort = {
        physicalPort: localInterface!.id,
        mode: IpPortMode.generator,
        resolution: defaultResolution,
        frameRate: defaultFrameRate,
        address: '127.0.0.1',
        port: '' as unknown as number,
        scanMode: defaultScanMode,
        timestampResolution: defaultTimestampResolution,
        _port: { ...localInterface, _appliance: localInterface.appliance },
      }
      setFieldValue(`${namePrefix}.ports.0`, { ...generatorInputPort, id: logicalPort1.id })
    }
  }, [setFieldValue, namePrefix, logicalPort1, mode, appliancePhysicalPorts])
  // <-- End hack that should be removed

  const addNewLogicalPortToForm = () => {
    const defaultPhysicalPort: PhysicalPort | undefined =
      (logicalPort1?._port ||
        appliancePhysicalPorts?.find((physicalPort) => {
          const occupiedReason = whyIsPhysicalPortUnavailable({ physicalPort, requestee: { groupId } })
          return occupiedReason === undefined
        })) ??
      appliancePhysicalPorts?.[0]

    if (!defaultPhysicalPort) {
      return
    }
    const logicalPortInit = {
      physicalPortId: defaultPhysicalPort.id,
      port: defaultPhysicalPort,
      enforcedMode: areMultipleInterfacesSupported ? (logicalPort1.mode as IpPortMode) : undefined,
      settings: selectedAppliance.settings,
    }
    const nextIndex = logicalPorts.length
    setFieldValue(
      `${namePrefix}.ports.${nextIndex}`,
      isInputForm ? initialInputLogicalPort(logicalPortInit) : initialOutputLogicalPort(logicalPortInit),
    )
    setFieldError(`${namePrefix}.ports.${nextIndex}`, '')
  }

  useEffect(() => {
    const shouldAutoAddNewLogicalPortSection = logicalPorts.length === 0
    if (shouldAutoAddNewLogicalPortSection) {
      addNewLogicalPortToForm()
    }
  }, [appliancePhysicalPorts])

  return (
    <>
      {isFetchingPhysicalPorts && <Loading message={`Fetching interfaces...`} />}
      {listPhysicalPortsError && <Error message={`Failed fetching interfaces`} />}

      {appliancePhysicalPorts && appliancePhysicalPorts.length === 0 && (
        <GridItem newLine>
          <Typography component="div" variant="body1">
            The selected appliance has no available interfaces.
          </Typography>
        </GridItem>
      )}

      {appliancePhysicalPorts && appliancePhysicalPorts.length > 0 && (
        <LogicalPortForm
          isRegional={false}
          namePrefix={namePrefix}
          logicalPorts={logicalPorts}
          isInputForm={isInputForm}
          adminStatus={adminStatus}
          appliancePhysicalPorts={appliancePhysicalPorts}
          isModeSelectionDisabled={isModeSelectionDisabled}
          enforcedPortMode={enforcedPortMode}
          restrictPhysicalPortsToGroupId={groupId}
          onAddLogicalPortRequested={onAddLogicalPortRequested}
        />
      )}
    </>
  )
}

export const ReferencedComponent = forwardRef(Component)

export const ApplianceInterfaceSection = <T extends ApplianceSectionForm>({
  myRef,
  ...rest
}: FormikProps<T> & Props & { myRef: React.Ref<{ onAddInterfaceButtonClicked: () => void }> }) => (
  <ReferencedComponent {...(rest as any)} ref={myRef} />
)
